/**
 * @file The fields of a combined history row, shared by the table, the
 *   cards, and the CSV export.
 */
import React from 'react';
import { format } from 'date-fns';
import {
  formatDateParsedZoneLong,
  formatDateParsedZoneShort,
} from 'platform/utilities/date/index';
import { VBMS_STATUS_LABELS, vbmsStatusLabel } from './SubmissionCard';
import { SUBMISSION_TYPES, readDate } from '../utilities/combinedHistory';
import { INTENT_TO_FILE_FORM } from '../utilities/constants';

/** @typedef {import('../utilities/combinedHistory').CombinedHistorySubmission} Submission */

/**
 * Starts every text sort value. va-table picks each column's comparator from
 * its first value: numeric if `parseFloat` reads it, date if `new Date` does,
 * otherwise alphabetical. A first value like the surname May would pick a
 * date sort, and a UUID like 5e1d0001-... a number sort. Chrome's date parser
 * skips an unknown leading word, so "text Aug 12" is still a date, but it
 * rejects a word with a digit in it. Neither parser reads "text0|Aug 12", so
 * the column sorts alphabetically.
 */
const TEXT_SORT_PREFIX = 'text0|';

/**
 * @param {string} value The text to sort by.
 * @returns {string} A sort value va-table always compares as text.
 */
export const textSortValue = value => `${TEXT_SORT_PREFIX}${value}`;

/**
 * @param {{ firstName: string, lastName: string }} submission
 * @returns {string} The claimant's name as "Last, First", or the one name
 *   the source gave.
 */
export const claimantName = submission =>
  [submission.lastName, submission.firstName].filter(Boolean).join(', ');

/**
 * @param {{ claimantId?: string|null, firstName: string, lastName: string }} submission
 * @returns {JSX.Element|string} The name as a link to the claimant overview,
 *   or as text for a row without a claimant ID. A row without a name is blank.
 */
export const claimantLink = submission => {
  const name = claimantName(submission);
  return submission.claimantId && name ? (
    <va-link
      href={`/representative/find-claimant/claimant-overview/${submission.claimantId}`}
      label={`${name}, go to the claimant overview`}
      text={name}
    />
  ) : (
    name
  );
};

/**
 * A masked SSN or file number, for example *****1234.
 *
 * @type {RegExp}
 */
const MASKED_IDENTIFIER = /^\*+\d{4}$/;

/**
 * @param {{ maskedIdentifier?: string|null }} submission
 * @returns {string} The identifier when the API sent it masked, or empty.
 *   The browser never masks one itself, so an unmasked value is dropped.
 */
export const maskedIdentifier = submission =>
  MASKED_IDENTIFIER.test(submission.maskedIdentifier ?? '')
    ? submission.maskedIdentifier
    : '';

/**
 * @param {string|null|undefined} isoDate
 * @returns {string} The date as MM/DD/YYYY, or empty for a missing or
 *   unreadable one, so a bad date from an API can't take the history down.
 */
export const shortDateOrBlank = isoDate =>
  readDate(isoDate) ? formatDateParsedZoneShort(isoDate) : '';

/**
 * @param {string|null|undefined} isoDate
 * @returns {string} The date as "June 2, 2026", or empty for a missing or
 *   unreadable one.
 */
export const longDateOrBlank = isoDate =>
  readDate(isoDate) ? formatDateParsedZoneLong(isoDate) : '';

/**
 * @param {string|null|undefined} isoDate
 * @returns {string} The date as YYYY-MM-DD, or empty for a missing or
 *   unreadable one. The export uses it because a spreadsheet in a non-US
 *   locale reads MM/DD/YYYY as DD/MM/YYYY, quoted or not, while every
 *   locale reads an ISO date the same way.
 */
export const isoDateOrBlank = isoDate => {
  const date = readDate(isoDate);
  return date ? format(date, 'yyyy-MM-dd') : '';
};

/**
 * The design's badge colors, as VADS utility classes, and icon by VBMS
 * status. Keyed by status rather than label, so rewording a label keeps its
 * look. The Received border is blue, as in the design.
 */
const BADGE_RECEIVED = {
  colors:
    'vads-u-color--success-darker vads-u-background-color--success-lighter vads-u-border-color--info-darker',
  icon: 'check_circle',
};
const BADGE_AWAITING = {
  colors:
    'vads-u-color--info-darker vads-u-background-color--info-lighter vads-u-border-color--info-darker',
  icon: 'info',
};
const BADGE_ERROR = {
  colors:
    'vads-u-color--error-dark vads-u-background-color--error-lighter vads-u-border-color--error-dark',
  icon: 'warning',
};
/** @type {ReadonlyMap<string, { colors: string, icon: string }>} */
const BADGES = new Map([
  ['received', BADGE_RECEIVED],
  ['awaiting_receipt', BADGE_AWAITING],
  ['awaiting_receipt_warning', BADGE_ERROR],
  ['processing_error', BADGE_ERROR],
]);

/**
 * The design's status badge: a bordered box with an icon before its label.
 * va-tag-status is close, but its text is not semibold, its border takes the
 * text color, and its error icon is not the design's triangle.
 * `.submissions__status-badge` in sass/SubmissionsPage.scss sets what no
 * utility class can.
 *
 * @param {{ vbmsStatus: string }} submission
 * @returns {JSX.Element}
 */
const statusBadge = submission => {
  const { colors, icon } = BADGES.get(submission.vbmsStatus) ?? BADGE_AWAITING;
  return (
    <span
      className={`submissions__status-badge vads-u-align-items--center vads-u-padding-y--0p5 vads-u-padding-left--0p5 vads-u-padding-right--1 vads-u-border--1px vads-u-font-size--base vads-u-font-weight--bold vads-u-line-height--1 ${colors}`}
    >
      <va-icon
        class="vads-u-margin-right--0p5"
        icon={icon}
        size="3"
        aria-hidden="true"
      />
      {vbmsStatusLabel(submission)}
    </span>
  );
};

/**
 * @param {string|null|undefined} isoDate
 * @returns {string} "Received June 2, 2026", or empty for a missing or
 *   unreadable date.
 */
const receivedOnOrBlank = isoDate => {
  const date = longDateOrBlank(isoDate);
  return date && `Received ${date}`;
};

/**
 * @param {string|number|null|undefined} confirmationNumber
 * @returns {string} The confirmation number's sort value. A numeric number is
 *   zero-padded so a 7-digit number sorts after a 6-digit one.
 */
const confirmationSortValue = confirmationNumber => {
  const value = String(confirmationNumber ?? '');
  return textSortValue(/^\d+$/.test(value) ? value.padStart(12, '0') : value);
};

/**
 * The Files / Form column's two groups. A row's sort value starts with its
 * group, so every upload's file count sorts ahead of every form number.
 */
const FILE_COUNT_GROUP = '1-file-count|';
const FORM_NUMBER_GROUP = '2-form-number|';

/**
 * @param {Submission} submission
 * @returns {number|null} An upload's file count: the files it lists when it
 *   has a list, so the count matches the Files modal, otherwise the count
 *   QuickSubmit reports. Null when it has neither.
 */
export const fileCount = submission =>
  submission.documents?.length > 0
    ? submission.documents.length
    : submission.numberOfFiles ?? null;

/**
 * The Files / Form value: an upload's file count, as "1 file" or "6 files",
 * or a form's number. A count is zero-padded so counts sort in order as text,
 * and a missing value sorts first within its group.
 *
 * @param {Submission} submission
 * @returns {{ sortValue: string, text: string }} The value as shown, and
 *   its text sort value.
 */
export const filesOrFormField = submission => {
  if (submission.type === SUBMISSION_TYPES.QUICK_SUBMIT) {
    const count = String(fileCount(submission) ?? '');
    return {
      sortValue: textSortValue(
        `${FILE_COUNT_GROUP}${count.padStart(count ? 4 : 0, '0')}`,
      ),
      text: count && `${count} ${count === '1' ? 'file' : 'files'}`,
    };
  }
  const form = submission.formType ?? '';
  return {
    sortValue: textSortValue(`${FORM_NUMBER_GROUP}${form}`),
    text: form,
  };
};

/**
 * @param {string|null|undefined} isoDate The date a received row sorts by.
 * @returns {string} "text0|Received|2026-09-02", so received rows sort together
 *   and by date.
 */
const receivedSortValue = isoDate =>
  textSortValue(`${VBMS_STATUS_LABELS.get('received')}|${isoDate ?? ''}`);

/**
 * @param {Submission} submission
 * @returns {string|null} The status label the row shows: the VBMS label,
 *   Received for an intent to file, or null for a QuickSubmit upload whose
 *   status has no VBMS equivalent, which shows QuickSubmit's own text or
 *   nothing. The status filter matches rows on this, so the filter and the
 *   cell cannot drift.
 */
export const statusLabel = submission => {
  if (
    !submission.vbmsStatus &&
    (submission.packetStatus ||
      submission.type === SUBMISSION_TYPES.QUICK_SUBMIT)
  ) {
    return null;
  }
  if (submission.formType === INTENT_TO_FILE_FORM) {
    return VBMS_STATUS_LABELS.get('received');
  }
  return vbmsStatusLabel(submission);
};

/**
 * A row's status: the badge, and for a Received badge the date line, such
 * as "Received May 3, 2026". An intent to file never reaches the VBMS
 * eFolder, so it shows as received on its submitted date. A QuickSubmit
 * status with no VBMS equivalent shows as reported, with no badge.
 *
 * @param {Submission} submission
 * @returns {{ sortValue: string, badge: JSX.Element|null, receivedOn: string, text: string }}
 *   The sort value, the badge, the date line or an empty string, and the
 *   status as plain text for the export, such as "Received May 3, 2026". A
 *   status sorts by its label, and received rows by their date within it.
 *   An upload with no status gets a null badge.
 */
export const statusField = submission => {
  const label = statusLabel(submission);

  // A QuickSubmit status with no VBMS equivalent shows as QuickSubmit reports
  // it, and an upload with no status shows nothing rather than a guess.
  if (label === null) {
    const packetStatus = submission.packetStatus || '';
    return {
      sortValue: textSortValue(packetStatus),
      badge: packetStatus ? <span>{packetStatus}</span> : null,
      receivedOn: '',
      text: packetStatus,
    };
  }

  if (submission.formType !== INTENT_TO_FILE_FORM) {
    const isReceived = submission.vbmsStatus === 'received';
    const receivedOn = isReceived
      ? receivedOnOrBlank(submission.vbmsReceivedDate)
      : '';
    return {
      // A received row sorts by the date it shows, or by the label alone
      // when it shows none.
      sortValue: isReceived
        ? receivedSortValue(receivedOn ? submission.vbmsReceivedDate : '')
        : textSortValue(label),
      badge: statusBadge(submission),
      receivedOn,
      text: receivedOn || label,
    };
  }

  const receivedOn = receivedOnOrBlank(submission.submittedDate);
  return {
    sortValue: receivedSortValue(receivedOn ? submission.submittedDate : ''),
    badge: statusBadge({ vbmsStatus: 'received' }),
    receivedOn,
    text: receivedOn || label,
  };
};

/**
 * The values va-table sorts each column by, in column order: Submission,
 * Claimant, Status, Confirmation #, and Files / Form. The Submission column
 * sorts by its date's timestamp, read with `readDate` as the loader's order
 * is. An undated row gets an empty value, which va-table treats as the
 * earliest date. Every other column uses `textSortValue`, so va-table sorts
 * it alphabetically.
 *
 * @param {Submission} submission
 * @param {{ status: { sortValue: string }, filesOrForm: { sortValue: string } }} fields
 *   The row's `statusField` and `filesOrFormField`, built once per row.
 * @returns {string[]} One sort value per column.
 */
export const sortValues = (submission, { status, filesOrForm }) => {
  const submitted = readDate(submission.submittedDate);
  return [
    submitted ? String(submitted.getTime()) : '',
    textSortValue(claimantName(submission)),
    status.sortValue,
    confirmationSortValue(submission.confirmationNumber),
    filesOrForm.sortValue,
  ];
};

/**
 * The export's header row. It has the table's values, with each stacked
 * cell split into columns of its own: Submission into Date and Type, and
 * Claimant into Claimant and Identifier.
 *
 * @type {readonly string[]}
 */
export const EXPORT_COLUMNS = Object.freeze([
  'Date',
  'Claimant',
  'Identifier',
  'Type',
  'Status',
  'Confirmation Number',
  'Files / Form',
]);

/**
 * @param {Submission[]} submissions Every row, in the sorted order, not
 *   only the page shown.
 * @returns {string[][]} The header row, then one row per submission with
 *   each value as the table shows it, except the date, which is ISO so a
 *   spreadsheet reads it the same in every locale. The identifier is the
 *   masked one or empty, never a value the API did not mask.
 */
export const exportRows = submissions => [
  [...EXPORT_COLUMNS],
  ...submissions.map(submission => [
    isoDateOrBlank(submission.submittedDate),
    claimantName(submission),
    maskedIdentifier(submission),
    submission.type ?? '',
    statusField(submission).text,
    submission.confirmationNumber ?? '',
    filesOrFormField(submission).text,
  ]),
];
